/**
 * Dext — where receipts go for the bookkeeper. See docs/architecture.md D13.
 *
 * Dext publishes no API for putting a document into an account. What every
 * account has is an *Extract by Email* address (`name@dext.cc`): anything
 * mailed to it with a file attached becomes an item in the Costs inbox. So
 * the integration is an address the organisation gives us and an email we
 * send, and there is no credential.
 *
 * What lives here is the part both sides have to agree on: what an address
 * looks like, which slips Dext will take, and what is recorded on an expense
 * once one has been sent.
 */
/** The domains Dext issues addresses on. `receipt-bank.com` is the old name. */
export const DEXT_DOMAINS = ["dext.cc", "multiple.dext.cc", "receipt-bank.com"];
const EMAIL = /^[^\s@<>(),;:"]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
/** A plain `local@domain` address — no display name, no list, no whitespace. */
export function isEmailAddress(value) {
    return typeof value === "string" && value.length <= 254 && EMAIL.test(value);
}
/**
 * Is this one of Dext's own addresses?
 *
 * Advisory, not a gate. Dext lets an account rename the part before the `@`
 * and has changed domain once already, and a bookkeeper may well want slips
 * sent to a shared mailbox that forwards on. The screen warns when this is
 * false; the API does not refuse.
 */
export function isDextAddress(value) {
    const domain = value.slice(value.lastIndexOf("@") + 1).toLowerCase();
    return DEXT_DOMAINS.includes(domain);
}
/**
 * Slip content types Dext extracts from. WebP is the absentee: BarOS accepts
 * it for a slip and Dext does not read it.
 */
export const DEXT_CONTENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
/** Dext's stated ceiling for an image. */
export const DEXT_MAX_IMAGE_BYTES = 6 * 1024 * 1024;
/**
 * The ceiling for a PDF. Dext states none; this is what keeps the message
 * under SES's 40 MB once base64 has added a third.
 */
export const DEXT_MAX_PDF_BYTES = 25 * 1024 * 1024;
/** Why a slip cannot go to Dext, or null when it can. */
export function dextRefusal(contentType, bytes) {
    if (!DEXT_CONTENT_TYPES.includes(contentType)) {
        return `Dext does not read ${contentType || "this kind of file"} — it takes JPEG, PNG and PDF.`;
    }
    const limit = contentType === "application/pdf" ? DEXT_MAX_PDF_BYTES : DEXT_MAX_IMAGE_BYTES;
    if (bytes > limit) {
        return `That slip is ${(bytes / 1024 / 1024).toFixed(1)} MB; Dext takes ${contentType === "application/pdf" ? "PDFs" : "images"} up to ${limit / 1024 / 1024} MB.`;
    }
    return null;
}
//# sourceMappingURL=dext.js.map